"use client"

import { Link, useLocation } from "react-router-dom"
import { type LucideIcon, ExternalLink } from "lucide-react"

import { cn } from "@/lib/utils"
import { Badge } from "@/components/ui/Badge"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar"

export interface NavItem {
  title: string
  url: string
  icon: LucideIcon
  badge?: number | string
  subtitle?: string
  isActive?: boolean
  external?: boolean
}

interface NavMainProps {
  items: NavItem[]
  label?: string
}

export function NavMain({ items, label }: NavMainProps) {
  const location = useLocation()

  return (
    <SidebarGroup>
      {label && (
        <SidebarGroupLabel>{label}</SidebarGroupLabel>
      )}
      <SidebarMenu>
        {items.map((item) => {
          // Prefix match, but the LONGEST matching sibling wins: on /edges/connectivity both
          // "Edges" (/edges) and "Connectivity" used to light up.
          const matches = (url: string) => location.pathname === url || (url !== '/' && location.pathname.startsWith(`${url}/`))
          const shadowed = items.some((o) => o !== item && o.url.length > item.url.length && o.url.startsWith(item.url) && matches(o.url))
          const isActive = item.isActive !== undefined
            ? item.isActive
            : matches(item.url) && !shadowed

          // For external links, use <a> tag with target="_blank"
          if (item.external) {
            return (
              <SidebarMenuItem key={item.url}>
                <SidebarMenuButton
                  asChild
                  tooltip={item.title}
                  isActive={false}
                >
                  <a href={item.url} target="_blank" rel="noopener noreferrer">
                    <item.icon className="size-4" />
                    <span className="flex-1">{item.title}</span>
                    <ExternalLink className="size-3 ml-auto text-muted-foreground" />
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            )
          }

          return (
            <SidebarMenuItem key={item.url}>
              <SidebarMenuButton
                asChild
                tooltip={item.title}
                isActive={isActive}
                className={cn(
                  isActive && "bg-sidebar-accent text-sidebar-accent-foreground"
                )}
              >
                <Link to={item.url}>
                  <item.icon className="size-4 shrink-0" />
                  <span className="flex-1 min-w-0">
                    <span className="block truncate">{item.title}</span>
                    {item.subtitle && (
                      <span className="block truncate text-xs text-muted-foreground leading-tight">
                        {item.subtitle}
                      </span>
                    )}
                  </span>
                  {item.badge !== undefined && (
                    <Badge variant="destructive" className="text-xs ml-auto">
                      {item.badge}
                    </Badge>
                  )}
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          )
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}
